import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { getApiBase } from '../lib/apiBase';


const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mjgpyqjv';
const API_URL = getApiBase();

export const Contact = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('idle');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [gdprError, setGdprError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!gdprConsent) { setGdprError(true); return; }
    setGdprError(false);
    setStatus('submitting');

    const form = e.target;
    const data = new FormData(form);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      });

      if (response.ok) {
        // Mirror to backend for admin panel tracking (fire-and-forget)
        try {
          await fetch(`${API_URL}/api/contact`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: data.get('name') || '',
              email: data.get('email') || '',
              company: data.get('organisation') || '',
              structure_type: data.get('subject') || '',
              description: data.get('message') || '',
              consent_data_processing: true,
            }),
          });
        } catch { /* admin mirror is non-blocking */ }
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen pt-16" style={{ fontFamily: 'IBM Plex Sans,sans-serif' }}>
        <SEO title={`${t('contact.title')} | Durable Designs`} path="/contact" />
        <section className="bg-green-900 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit,sans-serif' }}>{t('contact.title')}</h1>
          </div>
        </section>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up" data-testid="contact-success-inline" role="status">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-6" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Outfit,sans-serif' }}>{t('contact.success_title')}</h2>
            <p className="text-base text-gray-600 mb-8">{t('contact.success_inline')}</p>
            <Link to="/engineering" className="inline-flex items-center gap-2 bg-green-700 hover:bg-green-800 text-white px-6 py-3 text-sm font-medium tracking-wide transition-colors" data-testid="contact-success-home-btn">
              {t('contact.success_back')}
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16" style={{ fontFamily: 'IBM Plex Sans,sans-serif' }}>
      <SEO title={`${t('contact.title')} | Durable Designs`} description={t('contact.title')} path="/contact" />

      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ background: '#06120B' }}>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tighter mb-4" style={{ fontFamily: 'Plus Jakarta Sans,sans-serif' }} data-testid="contact-heading">
            {t('contact.title')}
          </h1>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-16">
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form" noValidate>
                <input type="hidden" name="_replyto" value="info@durabledesigns.co.uk" />
                <input type="hidden" name="_subject" value="New enquiry — Durable Designs" />

                <div>
                  <label htmlFor="name" className="block text-xs font-semibold tracking-[0.1em] uppercase text-gray-400 mb-2">{t('contact.label_name')} *</label>
                  <input id="name" type="text" name="name" required
                    className="w-full px-4 py-3 border border-gray-200 text-sm text-gray-900 bg-white outline-none focus:border-green-500 transition-colors" data-testid="contact-name-input" />
                </div>
                <div>
                  <label htmlFor="organisation" className="block text-xs font-semibold tracking-[0.1em] uppercase text-gray-400 mb-2">{t('contact.label_org')}</label>
                  <input id="organisation" type="text" name="organisation"
                    className="w-full px-4 py-3 border border-gray-200 text-sm text-gray-900 bg-white outline-none focus:border-green-500 transition-colors" data-testid="contact-organisation-input" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-semibold tracking-[0.1em] uppercase text-gray-400 mb-2">{t('contact.label_email')} *</label>
                  <input id="email" type="email" name="email" required
                    className="w-full px-4 py-3 border border-gray-200 text-sm text-gray-900 bg-white outline-none focus:border-green-500 transition-colors" data-testid="contact-email-input" />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-xs font-semibold tracking-[0.1em] uppercase text-gray-400 mb-2">{t('contact.label_subject')}</label>
                  <select id="subject" name="subject"
                    className="w-full px-4 py-3 border border-gray-200 text-sm text-gray-900 bg-white outline-none focus:border-green-500 transition-colors appearance-none" data-testid="contact-subject-select">
                    <option value="">{t('contact.select_placeholder')}</option>
                    <option value="Materials Consulting">Materials Consulting</option>
                    <option value="Infrastructure Risk">Infrastructure Risk</option>
                    <option value="Digital & AI">{t('contact.subject_digital')}</option>
                    <option value="General">{t('contact.subject_general')}</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs font-semibold tracking-[0.1em] uppercase text-gray-400 mb-2">{t('contact.label_message')} *</label>
                  <textarea id="message" name="message" required rows="6"
                    className="w-full px-4 py-3 border border-gray-200 text-sm text-gray-900 bg-white outline-none focus:border-green-500 transition-colors resize-none" data-testid="contact-message-input" />
                </div>

                <div data-testid="gdpr-consent-field">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" name="gdpr-consent" checked={gdprConsent}
                      onChange={(e) => { setGdprConsent(e.target.checked); if (e.target.checked) setGdprError(false); }}
                      required className="mt-0.5 w-4 h-4 accent-green-700 flex-shrink-0" data-testid="gdpr-consent-checkbox" />
                    <span className="text-sm text-gray-600 leading-relaxed">
                      {t('contact.gdpr_text')} {t('contact.gdpr_see')}{' '}
                      <Link to="/privacy-policy" className="text-green-700 hover:text-green-600 underline">{t('contact.gdpr_policy')}</Link>.
                    </span>
                  </label>
                  {gdprError && <p className="text-sm text-red-600 mt-2" data-testid="gdpr-error-message">{t('contact.gdpr_error')}</p>}
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-600" role="alert" data-testid="form-error-message">
                    {t('contact.error_inline')}{' '}
                    <a href="mailto:info@durabledesigns.co.uk" className="text-green-700 underline">info@durabledesigns.co.uk</a>.
                  </p>
                )}

                <button type="submit" disabled={status === 'submitting'}
                  className="bg-green-700 hover:bg-green-800 disabled:bg-gray-400 text-white px-8 py-3 text-sm font-medium tracking-wide transition-colors" data-testid="contact-submit-button">
                  {status === 'submitting' ? t('contact.submitting') : t('contact.submit')}
                </button>
              </form>
            </div>

            <div className="lg:col-span-2">
              <div className="space-y-8">
                <div data-testid="contact-registered-entity">
                  <h3 className="text-xs font-semibold tracking-[0.15em] uppercase text-gray-400 mb-4" style={{ fontFamily: 'Outfit,sans-serif' }}>Registered Business</h3>
                  <p className="text-sm text-gray-700 font-medium leading-relaxed">Concrete Control LLC <span className="text-gray-500 font-normal">(dba Durable Designs)</span></p>
                  <p className="text-sm text-gray-600 leading-relaxed mt-1">1209 Mountain Road Pl NE, Ste N<br />Albuquerque, NM 87110, USA</p>
                </div>
                <div>
                  <h3 className="text-xs font-semibold tracking-[0.15em] uppercase text-gray-400 mb-4" style={{ fontFamily: 'Outfit,sans-serif' }}>{t('contact.email_label')}</h3>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <a href="mailto:info@durabledesigns.co.uk" className="text-sm text-green-700 hover:text-green-600 font-medium transition-colors" data-testid="contact-email-link">info@durabledesigns.co.uk</a>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 leading-relaxed">Contact via email. Durable Designs, operated by Concrete Control LLC, responds to enquiries within two business days.</p>
                </div>
                <div>
                  <h3 className="text-xs font-semibold tracking-[0.15em] uppercase text-gray-400 mb-4" style={{ fontFamily: 'Outfit,sans-serif' }}>{t('contact.telephone_label')}</h3>
                  <div className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                    <div className="text-sm text-gray-600 space-y-1.5" data-testid="contact-phone-list">
                      <p><span className="inline-block w-6 text-xs text-gray-400 font-medium tracking-wide">US</span> <a href="tel:+13072203722" className="hover:text-green-700 transition-colors">+1 (307) 220-3722</a></p>
                      <p><span className="inline-block w-6 text-xs text-gray-400 font-medium tracking-wide">UK</span> <a href="tel:+447411241994" className="hover:text-green-700 transition-colors">+44 74 1124 1994</a></p>
                      <p><span className="inline-block w-6 text-xs text-gray-400 font-medium tracking-wide">PT</span> <a href="tel:+351931498376" className="hover:text-green-700 transition-colors">+351 931 498 376</a></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default Contact;
